/**
 * Custom files dropzone
 *
 * @author Sebastiano Lovatin <sebastiano@gestionaleauto.com>
 */

import React, { memo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';

const useStyles = makeStyles((theme) => ({
  fileChipWrapper: {
    padding: '5px 5px 5px 0',
    '& .MuiButtonBase-root': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: 5,
      height: 27,
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.contrastText,
        '&:hover': {
          color: theme.palette.primary.contrastText,
        },
      },
    },
  },
  fileIconReadOnly: {
    fontSize: '25px !important',
  },
  filenameReadOnly: {
    fontSize: 14,
    marginTop: 'auto',
  },
}));

/**
 * @param file {object} File from uploader component
 * @param readOnly {boolean} File is in reaOnly mode
 * @param onRemove {function} On remove file click button
 *
 * @return {JSX.Element}
 * @constructor
 */
const File = memo(({ file, readOnly, onRemove }) => {
  const classes = useStyles();
  if (readOnly) {
    return (
      <div className="d-flex mr-2 mt-2">
        <DescriptionIcon color="primary" className={classes.fileIconReadOnly} />
        <div className={classes.filenameReadOnly}>{file.name}</div>
      </div>
    );
  }
  if (file.loading) {
    return (
      <div className={classes.fileChipWrapper}>
        <Skeleton variant="text" width={100} height={27} />
      </div>
    );
  }
  return (
    <div className={`${classes.fileChipWrapper}`}>
      <Chip
        label={file.name}
        deleteIcon={<CloseIcon />}
        onDelete={() => {
          onRemove(file);
        }}
      />
    </div>
  );
}, (prevProps, nextProps) => _.isEqual(
  _.omit(prevProps, ['onRemove']),
  _.omit(nextProps, ['onRemove']),
));

File.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    loading: PropTypes.bool,
  }),
  readOnly: PropTypes.bool,
  onRemove: PropTypes.func,
};

File.defaultProps = {
  file: {},
  readOnly: false,
  onRemove: () => {},
};

export default File;
